import { AsyncResult, Atom, Reactivity } from 'effect/reactivity';
import {
  Clock,
  Duration,
  Effect,
  Fiber,
  Layer,
  Option,
  Predicate,
  Schema,
  type Scope,
} from 'effect';
import type { AtomRegistry } from 'effect/reactivity';
import { apiTransportAtom } from '@listening/api-contract/api-client-tag';
import type { HttpClient } from 'effect/http';
import type { ReactivityKey } from './reactivity-keys';
import type { AppServices } from './runtime-context';

/**
 * The shared data layer's runtime. Every read is an atom built with `apiQuery`
 * and every write an atom built with `apiMutation`; both run on the services of
 * the app that owns the registry. Each app seeds its registry once with
 * `appDataInitialValues`: start in `router.tsx` (one registry per SSR request
 * and one per tab), mobile in `mobileAtomRegistry`, the browser extension per
 * React root.
 */
const missingAppLayer: Layer.Layer<AppServices> = Layer.effectContext(
  Effect.die(
    'The atom registry was not seeded with appDataInitialValues, so data atoms have no API client.',
  ),
);

const appLayerAtom = Atom.make(missingAppLayer).pipe(Atom.keepAlive);

/**
 * Whether the app is in front of the user: the tab is visible on web, the app
 * is active on mobile. Polls pause while it is false, and turning true
 * refetches stale queries.
 */
const appVisibilitySourceAtom = Atom.make<Atom.Atom<boolean>>(
  Atom.make(false),
).pipe(Atom.keepAlive);

export const appVisibleAtom = Atom.readable((get) =>
  get(get(appVisibilitySourceAtom)),
);

const appFocusSignal = Atom.readable((get) => {
  let count = 0;
  get.subscribe(appVisibleAtom, (visible) => {
    if (visible) get.setSelf(++count);
  });
  get.once(appVisibleAtom);
  return count;
});

/** The tab's visibility, for web registries; the server has none. */
export const documentVisibleAtom = Atom.readable((get) => {
  if (typeof document === 'undefined') return false;
  const update = () => get.setSelf(document.visibilityState === 'visible');
  document.addEventListener('visibilitychange', update);
  get.addFinalizer(() =>
    document.removeEventListener('visibilitychange', update),
  );
  return document.visibilityState === 'visible';
});

/** Seeds a registry with the app's services, its visibility signal, and
 * where its API requests go: `apiTransport` of the options its
 * `ApiClient.layer` takes, over the app's own `HttpClient`. */
export const appDataInitialValues = (platform: {
  readonly layer: Layer.Layer<AppServices>;
  readonly visible: Atom.Atom<boolean>;
  readonly api: Layer.Layer<
    HttpClient.HttpClient,
    never,
    HttpClient.HttpClient
  >;
}) =>
  [
    [appLayerAtom, platform.layer],
    [appVisibilitySourceAtom, platform.visible],
    [apiTransportAtom, platform.api.pipe(Layer.provide(platform.layer))],
  ] as const;

export const apiRuntime = Atom.runtime((get) => get(appLayerAtom));

export type ApiServices =
  | AppServices
  | Scope.Scope
  | AtomRegistry.AtomRegistry
  | Reactivity.Reactivity;

/** How long a query's value is kept once nothing shows it. */
export const defaultTimeToLive = Duration.minutes(5);

export type ApiQueryOptions = {
  /** Invalidating any of these keys refetches the query. */
  readonly reactivityKeys?: ReadonlyArray<ReactivityKey>;
  /** How long a value counts as fresh. Mounting or refocusing refetches it
   * after this; the default refetches every time. */
  readonly staleTime?: Duration.Input;
  readonly timeToLive?: Duration.Input;
};

type QueryEffect<A, E> =
  | Effect.Effect<A, E, ApiServices>
  | ((get: Atom.AtomContext) => Effect.Effect<A, E, ApiServices>);

/**
 * When the signed-in user last changed. Every query refetches when it moves,
 * and hides what it fetched before it, so one user's data is never shown to
 * the next.
 */
export const apiDataEpochAtom = Atom.make({ startedAt: 0 }).pipe(
  Atom.keepAlive,
);

/** Drops every query's data, as on sign-out. */
export const clearApiData = (registry: AtomRegistry.AtomRegistry) =>
  registry.set(apiDataEpochAtom, {
    startedAt: Effect.runSync(Clock.currentTimeMillis),
  });

const queryAtom = <A, E>(create: QueryEffect<A, E>) =>
  apiRuntime.atom((get) => {
    get(apiDataEpochAtom);
    return Predicate.isFunction(create) ? create(get) : create;
  });

const isBefore =
  (startedAt: number) =>
  <A, E>(success: AsyncResult.Success<A, E>) =>
    success.timestamp < startedAt;

const hidePreviousEpoch = <A, E>(
  result: AsyncResult.AsyncResult<A, E>,
  startedAt: number,
): AsyncResult.AsyncResult<A, E> => {
  if (AsyncResult.isSuccess(result) && isBefore(startedAt)(result))
    return AsyncResult.initial(true);
  if (
    AsyncResult.isFailure(result) &&
    Option.exists(result.previousSuccess, isBefore(startedAt))
  )
    return AsyncResult.failure(result.cause, { waiting: result.waiting });
  return result;
};

/** A refetch that is still running or that failed keeps what was shown,
 * which may be a written value newer than the source's last fetch. */
const keepShownValue = <A, E>(
  result: AsyncResult.AsyncResult<A, E>,
  shown: Option.Option<AsyncResult.AsyncResult<A, E>>,
): AsyncResult.AsyncResult<A, E> => {
  if (AsyncResult.isFailure(result))
    return AsyncResult.failureWithPrevious(result.cause, {
      previous: Option.orElse(shown, () => Option.some(result)),
      waiting: result.waiting,
    });
  if (!result.waiting) return result;
  return Option.match(Option.filter(shown, AsyncResult.isSuccess), {
    onNone: () => result,
    onSome: AsyncResult.waiting,
  });
};

/**
 * `source`, plus writes: writing a value shows it until the next fetch
 * succeeds. It keeps the source's time to live, so a written value outlives
 * the screen that wrote it. A write to a query nothing has read yet starts
 * its fetch, so later fetches still reach it.
 */
const withWrites = <A, E>(
  source: Atom.Atom<AsyncResult.AsyncResult<A, E>>,
  timeToLive: Duration.Input,
) => {
  const written: Atom.Writable<
    AsyncResult.AsyncResult<A, E>,
    A
  > = Atom.writable(
    (get) => keepShownValue(get(source), get.self()),
    (ctx, value: A) => {
      ctx.get(written);
      ctx.setSelf(AsyncResult.success(value));
    },
    (refresh) => refresh(source),
  ).pipe(Atom.setIdleTTL(timeToLive));
  return written;
};

const reactivityAtom = apiRuntime.atom(Effect.service(Reactivity.Reactivity));

/**
 * Refetches `self` when one of `keys` is invalidated. Unlike the runtime's
 * `withReactivity`, it reads `self` as a dependency, so when it expires the
 * nodes under it go with it instead of each starting its own time to live.
 */
const refreshOnKeys =
  (keys: ReadonlyArray<ReactivityKey>) =>
  <A, W>(self: Atom.Writable<A, W>) =>
    Atom.transform(
      self,
      (get) => {
        const reactivity = get(reactivityAtom);
        if (AsyncResult.isSuccess(reactivity))
          get.addFinalizer(
            reactivity.value.registerUnsafe(keys, () => get.refresh(self)),
          );
        return get(self);
      },
      { initialValueTarget: self },
    );

/**
 * Makes `source` writable and refetch on its reactivity keys and when it is
 * stale on mount or refocus. Every node keeps the source's time to live: a
 * reactivity node that expired sooner would miss an invalidation while its
 * list is off screen.
 */
const withQueryRefresh =
  (options: ApiQueryOptions) =>
  <A, E>(source: Atom.Atom<AsyncResult.AsyncResult<A, E>>) => {
    const timeToLive = options.timeToLive ?? defaultTimeToLive;
    const cached = source.pipe(Atom.setIdleTTL(timeToLive));
    const kept = withWrites(cached, timeToLive);
    const reactive =
      options.reactivityKeys === undefined
        ? kept
        : kept.pipe(
            refreshOnKeys(options.reactivityKeys),
            Atom.setIdleTTL(timeToLive),
          );
    return reactive.pipe(
      Atom.swr({
        staleTime: options.staleTime ?? Duration.zero,
        revalidateOnFocus: true,
        focusSignal: appFocusSignal,
      }),
      Atom.transform(
        (get, atom) =>
          hidePreviousEpoch(get(atom), get(apiDataEpochAtom).startedAt),
        // A registry's initial value for the query seeds the cached read,
        // which then counts as fetched.
        { initialValueTarget: cached },
      ),
    );
  };

/** A server read. Mount it with `useAtomValue`. */
export const apiQuery = <A, E>(
  create: QueryEffect<A, E>,
  options: ApiQueryOptions = {},
) => queryAtom(create).pipe(withQueryRefresh(options));

/**
 * A server read the server renders. Its value travels with the page under
 * `key`, so the browser starts from it instead of fetching again. A failure
 * stays on the server, and the browser fetches again.
 */
export const ssrApiQuery = <A, E>(
  create: QueryEffect<A, E>,
  options: ApiQueryOptions & {
    readonly key: string;
    readonly success: Schema.ConstraintCodec<A, unknown>;
  },
) =>
  queryAtom(create).pipe(
    Atom.serializable({
      key: options.key,
      schema: AsyncResult.Schema({ success: options.success }),
      skipInitialRun: true,
    }),
    withQueryRefresh(options),
  );

export type InfinitePages<Page> = {
  readonly pages: ReadonlyArray<Page>;
  readonly hasNextPage: boolean;
  /** A page past the loaded ones is being fetched. */
  readonly loadingMore: boolean;
};

type LoadedPages<Page, Param> = {
  readonly pages: ReadonlyArray<Page>;
  readonly nextParam: Param | undefined;
  readonly epoch: number;
};

type InfiniteQueryOptions<Page, Param, E> = ApiQueryOptions & {
  readonly initialParam: Param;
  readonly fetchPage: (param: Param) => Effect.Effect<Page, E, ApiServices>;
  readonly nextParam: (page: Page) => Param | undefined;
};

type InfiniteQuerySsr<Page, Param> = {
  readonly key: string;
  readonly page: Schema.ConstraintCodec<Page, unknown>;
  readonly param: Schema.ConstraintCodec<Param, unknown>;
};

const infiniteQuery = <Page, Param, E>(
  options: InfiniteQueryOptions<Page, Param, E>,
  ssr?: InfiniteQuerySsr<Page, Param>,
) => {
  const timeToLive = options.timeToLive ?? defaultTimeToLive;
  const requestedPages = Atom.make(1).pipe(Atom.setIdleTTL(timeToLive));
  // Read outside the effect: a hydrated value skips the effect's first run,
  // and only the factory's reads are tracked until it runs.
  const loaded = apiRuntime.atom((get) => {
    const epoch = get(apiDataEpochAtom).startedAt;
    const wanted = get(requestedPages);
    return Effect.gen(function* () {
      const previous = get
        .self<AsyncResult.AsyncResult<LoadedPages<Page, Param>, E>>()
        .pipe(
          Option.flatMap(AsyncResult.value),
          Option.filter((value) => value.epoch === epoch),
          Option.getOrUndefined,
        );
      const appending =
        previous !== undefined &&
        previous.pages.length < wanted &&
        previous.nextParam !== undefined;
      const pages = appending ? [...previous.pages] : [];
      let param: Param | undefined = appending
        ? previous.nextParam
        : options.initialParam;
      while (param !== undefined && pages.length < wanted) {
        const page = yield* options.fetchPage(param);
        pages.push(page);
        param = options.nextParam(page);
      }
      const loadedPages: LoadedPages<Page, Param> = {
        pages,
        nextParam: param,
        epoch,
      };
      return loadedPages;
    });
  });
  const source = (
    ssr === undefined
      ? loaded
      : loaded.pipe(
          Atom.serializable({
            key: ssr.key,
            schema: AsyncResult.Schema({
              success: Schema.Struct({
                pages: Schema.Array(ssr.page),
                nextParam: Schema.UndefinedOr(ssr.param),
                epoch: Schema.Finite,
              }),
            }),
            skipInitialRun: true,
          }),
        )
  ).pipe(withQueryRefresh(options));
  const list = Atom.writable(
    (get): AsyncResult.AsyncResult<InfinitePages<Page>, E> => {
      const result = get(source);
      const wanted = get(requestedPages);
      return AsyncResult.map(result, (value) => ({
        pages: value.pages,
        hasNextPage: value.nextParam !== undefined,
        loadingMore: result.waiting && value.pages.length < wanted,
      }));
    },
    (ctx) => {
      const result = ctx.get(source);
      const value = AsyncResult.value(result).pipe(Option.getOrUndefined);
      if (result.waiting || value?.nextParam === undefined) return;
      ctx.set(requestedPages, value.pages.length + 1);
    },
    (refresh) => refresh(source),
  );
  return { list, source };
};

/**
 * A paged server read. Writing to it loads the next page, unless one is
 * already loading or there is none. A refetch reloads every page already
 * loaded, one after another, so the list keeps its length.
 */
export const apiInfiniteQuery = <Page, Param, E>(
  options: InfiniteQueryOptions<Page, Param, E>,
) => infiniteQuery(options).list;

/**
 * A paged server read the server renders: `list` as `apiInfiniteQuery`'s, and
 * `source`, the loaded pages it reads, which travel with the page under
 * `key`. A route loader starts `source`, since `list` reads other atoms too.
 */
export const ssrApiInfiniteQuery = <Page, Param, E>(
  options: InfiniteQueryOptions<Page, Param, E> & InfiniteQuerySsr<Page, Param>,
) => infiniteQuery(options, options);

/**
 * A server write. Calls run side by side; a plain `Atom.fn` would interrupt
 * the previous call. A call that succeeds invalidates `reactivityKeys`. A call
 * finishes even when the screen that made it closes first, so a sheet can
 * save and close at once.
 */
export const apiMutation = <Arg = void, A = never, E = never>(
  fn: (arg: Arg, get: Atom.FnContext) => Effect.Effect<A, E, ApiServices>,
  options: { readonly reactivityKeys?: ReadonlyArray<ReactivityKey> } = {},
) =>
  apiRuntime.fn<Arg>()(
    (arg, get) => {
      const write = fn(arg, get);
      return Effect.flatMap(
        Effect.forkDetach(
          options.reactivityKeys === undefined
            ? write
            : Reactivity.mutation(write, options.reactivityKeys),
        ),
        Fiber.join,
      );
    },
    { concurrent: true },
  );

/**
 * Shows `update` applied to a loaded query while `write` runs, and puts the
 * value back if it fails. A query that has not loaded is left alone; the
 * write is sent regardless.
 */
export const withOptimisticUpdate = <A, QE, B, E, R>(
  atom: Atom.Writable<AsyncResult.AsyncResult<A, QE>, A>,
  update: (value: A) => A,
  write: Effect.Effect<B, E, R>,
) =>
  Effect.gen(function* () {
    const before = AsyncResult.value(yield* Atom.get(atom));
    const restore = Option.match(before, {
      onNone: () => Effect.void,
      onSome: (value) => Atom.set(atom, value),
    });
    if (Option.isSome(before)) yield* Atom.set(atom, update(before.value));
    return yield* Effect.tapError(write, () => restore);
  });

/** Refetches every query tagged with one of the keys it is called with. */
export const invalidateApiKeys = apiMutation(
  (keys: ReadonlyArray<ReactivityKey>) => Reactivity.invalidate(keys),
);

/** Refetches the queries tagged with `keys`, from outside an atom. */
export const invalidateApiData = (
  registry: AtomRegistry.AtomRegistry,
  keys: ReadonlyArray<ReactivityKey>,
) => {
  const release = registry.mount(invalidateApiKeys);
  registry.set(invalidateApiKeys, keys);
  release();
};

/** A registry initial value that starts `atom` as already fetched. */
export const seedQuery = <A, E>(
  atom: Atom.Atom<AsyncResult.AsyncResult<A, E>>,
  value: A,
) => Atom.initialValue(atom, AsyncResult.success(value));

/** What a disabled query reads: nothing, and never a fetch. */
export const idleQuery = Atom.make(AsyncResult.initial(false));

/**
 * Refetches `self` after `interval(value, failed, read)` milliseconds for as
 * long as something shows it and the app is visible; `false` stops polling
 * until the next result. An atom `interval` reads through `read` reschedules
 * the poll when it changes. Polling never runs during SSR, where nothing is
 * visible.
 */
export const pollEvery =
  <A>(
    interval: (
      value: Option.Option<A>,
      failed: boolean,
      read: <B>(atom: Atom.Atom<B>) => B,
    ) => number | false,
  ) =>
  <E, W>(self: Atom.Writable<AsyncResult.AsyncResult<A, E>, W>) =>
    Atom.transform(
      self,
      (get): AsyncResult.AsyncResult<A, E> => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const watched = new Set<Atom.Atom<unknown>>();
        const read = <B>(atom: Atom.Atom<B>) => {
          if (!watched.has(atom)) {
            watched.add(atom);
            get.subscribe(atom, schedule);
          }
          return get.once(atom);
        };
        const schedule = () => {
          clearTimeout(timer);
          const result = get.once(self);
          if (result.waiting || !get.once(appVisibleAtom)) return;
          const delay = interval(
            AsyncResult.value(result),
            AsyncResult.isFailure(result),
            read,
          );
          if (delay === false) return;
          timer = setTimeout(() => get.refresh(self), delay);
        };
        get.addFinalizer(() => clearTimeout(timer));
        get.subscribe(self, (result) => {
          get.setSelf(result);
          schedule();
        });
        get.subscribe(appVisibleAtom, schedule);
        schedule();
        return get.once(self);
      },
      { initialValueTarget: self },
    );
